import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Choose a New Password | Finendra" },
      {
        name: "description",
        content: "Set a new password for your Finendra team account.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResetPasswordPage,
});

const schema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters").max(72),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // The recovery link puts a session in place before this page renders.
    supabase.auth.getSession().then(({ data }) => {
      setReady(Boolean(data.session));
    });
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = schema.safeParse({ password, confirm });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid password");
      return;
    }

    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: parsed.data.password,
      });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      await navigate({ to: "/admin", replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-24">
      <div className="w-full max-w-md rounded-3xl border border-brand-navy/10 bg-background p-10">
        <h1 className="font-display text-3xl font-bold text-brand-navy">Choose a new password</h1>

        {!ready ? (
          <p className="mt-4 text-sm text-brand-navy/60">
            This reset link is invalid or has expired. Request a new one from the{" "}
            <a href="/auth" className="font-bold text-brand-accent">
              sign-in page
            </a>
            .
          </p>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="password"
                className="text-xs font-bold uppercase tracking-widest text-brand-navy"
              >
                New password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-brand-navy outline-none focus:border-brand-accent"
                required
              />
            </div>

            <div>
              <label
                htmlFor="confirm"
                className="text-xs font-bold uppercase tracking-widest text-brand-navy"
              >
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-brand-navy outline-none focus:border-brand-accent"
                required
              />
            </div>

            {error && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-brand-navy px-6 py-4 text-sm font-bold text-background transition-[background-color,transform] duration-150 ease-out hover:bg-brand-accent active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none disabled:opacity-60 disabled:transform-none"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
